<template>
  <section class="mi" aria-labelledby="titulo-informado">
    <div class="mi-head">
      <p class="mi-eyebrow">No te pierdas nada</p>
      <h2 id="titulo-informado" class="mi-title">Mantente informado</h2>
      <p class="mi-sub">Horarios especiales, celebraciones y noticias de la parroquia, directamente en tu móvil.</p>
    </div>

    <div class="mi-grid">
      <!-- Avisos al móvil -->
      <article class="mi-card">
        <span class="mi-icon mi-icon--avisos" aria-hidden="true">
          <span class="material-symbols-outlined">{{ estado === "activos" ? "notifications_active" : "notifications" }}</span>
        </span>
        <div class="mi-card-body">
          <h3 class="mi-card-title">Avisos en el móvil</h3>
          <p class="mi-card-text">{{ textoAvisos }}</p>

          <ol v-if="estado === 'ios-instalar'" class="mi-pasos">
            <li>Pulsa <span class="material-symbols-outlined mi-inline">ios_share</span> <strong>Compartir</strong> en Safari.</li>
            <li>Elige <strong>«Añadir a pantalla de inicio»</strong>.</li>
            <li>Abre la web desde el nuevo icono y activa los avisos aquí.</li>
          </ol>

          <div class="mi-actions">
            <button
              v-if="estado === 'inactivos' || estado === 'cargando'"
              type="button"
              class="mi-btn"
              :disabled="trabajando || estado === 'cargando'"
              @click="activar"
            >
              <span class="material-symbols-outlined">notifications_active</span>
              {{ trabajando ? "Activando…" : "Activar avisos" }}
            </button>
            <template v-else-if="estado === 'activos'">
              <span class="mi-ok">
                <span class="material-symbols-outlined">check_circle</span> Avisos activados
              </span>
              <button type="button" class="mi-link" :disabled="trabajando" @click="desactivar">Desactivar</button>
            </template>
          </div>
          <p v-if="error" class="mi-error" role="alert">{{ error }}</p>
        </div>
      </article>

      <!-- Canal de WhatsApp -->
      <article class="mi-card" :class="{ 'is-pronto': !canalWhatsapp }">
        <span class="mi-icon mi-icon--whatsapp" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26"><path fill="currentColor" :d="iconoWhatsapp" /></svg>
        </span>
        <div class="mi-card-body">
          <h3 class="mi-card-title">
            Canal de WhatsApp
            <span v-if="!canalWhatsapp" class="mi-pill">Próximamente</span>
          </h3>
          <p class="mi-card-text">
            {{ canalWhatsapp
              ? "Síguenos en WhatsApp y recibe las novedades de la parroquia. Nadie verá tu número."
              : "Muy pronto podrás seguir a la parroquia en WhatsApp y recibir las novedades. Nadie verá tu número." }}
          </p>
          <div class="mi-actions">
            <a
              v-if="canalWhatsapp"
              :href="canalWhatsapp"
              target="_blank"
              rel="noopener"
              class="mi-btn mi-btn--whatsapp"
              v-track:seguir_whatsapp="{ origen: origen }"
            >
              <span class="material-symbols-outlined">add</span> Seguir el canal
            </a>
            <button v-else type="button" class="mi-btn mi-btn--whatsapp" disabled aria-disabled="true">
              <span class="material-symbols-outlined">schedule</span> Disponible pronto
            </button>
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { estadoAvisos, activarAvisos, desactivarAvisos } from "@/directives/avisosPush.js";
import { CANAL_WHATSAPP } from "@/contenido/config.js";

const TEXTOS = {
  cargando: "Recibe un aviso cuando haya una noticia importante o cambie un horario. Sin instalar nada.",
  inactivos: "Recibe un aviso cuando haya una noticia importante o cambie un horario. Sin instalar nada y sin dar tu teléfono.",
  activos: "Recibirás un aviso cuando la parroquia publique algo importante. Puedes desactivarlo cuando quieras.",
  bloqueado: "Has bloqueado los avisos de esta web. Para activarlos, permítelos en los ajustes del navegador (icono del candado junto a la dirección).",
  "ios-instalar": "En iPhone y iPad, Apple solo permite avisos si añades la web a tu pantalla de inicio:",
  "no-soportado": "Tu navegador no admite avisos. Prueba con Chrome, Edge, Firefox o Safari actualizados.",
  "no-disponible": "Los avisos estarán disponibles muy pronto.",
};

export default {
  name: "MantenteInformado",
  props: {
    origen: { type: String, default: "portada" },
  },
  data() {
    return {
      error: "",
      canalWhatsapp: CANAL_WHATSAPP,
      iconoWhatsapp:
        "M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2m.01 1.67c4.55 0 8.24 3.69 8.24 8.24 0 4.54-3.69 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.2 8.2 0 0 1-1.26-4.38c.01-4.54 3.7-8.23 8.24-8.23M8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.86-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18s.21-1.07.15-1.18c-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.47-.01",
    };
  },
  computed: {
    estado() {
      return estadoAvisos.estado;
    },
    trabajando() {
      return estadoAvisos.trabajando;
    },
    textoAvisos() {
      return TEXTOS[this.estado] || TEXTOS.inactivos;
    },
  },
  methods: {
    async activar() {
      this.error = "";
      try {
        await activarAvisos(this.origen);
      } catch (e) {
        this.error = "No se han podido activar los avisos. Inténtalo de nuevo en unos minutos.";
      }
    },
    async desactivar() {
      this.error = "";
      try {
        await desactivarAvisos(this.origen);
      } catch (e) {
        this.error = "No se han podido desactivar los avisos.";
      }
    },
  },
};
</script>

<style scoped>
.mi {
  padding: 56px 0 8px;
  text-align: left;
}

.mi-head {
  max-width: 640px;
  margin-bottom: 28px;
}

.mi-eyebrow {
  margin: 0 0 4px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--sd-gold);
}

.mi-title {
  margin: 0;
  font-family: var(--sd-font-display);
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 700;
  color: var(--sd-text);
}

.mi-sub {
  margin: 8px 0 0;
  color: var(--sd-muted);
}

.mi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 340px), 1fr));
  gap: 20px;
}

.mi-card {
  display: flex;
  gap: 18px;
  padding: 24px;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius);
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow);
}

.mi-icon {
  display: grid;
  flex-shrink: 0;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 16px;
}

.mi-icon .material-symbols-outlined {
  font-size: 28px;
  color: inherit;
}

.mi-icon--avisos {
  background: var(--sd-primary-soft);
  color: var(--sd-primary);
}

.mi-icon--whatsapp {
  background: rgba(37, 211, 102, 0.14);
  color: #128c4b;
}

.dark .mi-icon--whatsapp {
  color: #25d366;
}

.mi-card-body {
  flex: 1;
  min-width: 0;
}

.mi-card-title {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0 0 6px;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--sd-text);
}

.mi-pill {
  padding: 2px 10px;
  border-radius: 999px;
  background: var(--sd-gold-soft);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--sd-gold);
}

.mi-card-text {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--sd-muted);
}

.mi-pasos {
  margin: 10px 0 0;
  padding-left: 20px;
  font-size: 0.92rem;
  line-height: 1.6;
  color: var(--sd-text);
}

.mi-inline {
  font-size: 18px;
  vertical-align: -4px;
  color: var(--sd-primary);
}

.mi-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 16px;
}

.mi-actions:empty {
  display: none;
}

.mi-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 20px;
  border: 0;
  border-radius: 999px;
  background: var(--sd-primary);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--sd-primary-contrast);
  text-decoration: none;
  cursor: pointer;
  transition: transform 0.15s, background 0.15s, opacity 0.15s;
}

.mi-btn:hover:not(:disabled) {
  background: var(--sd-primary-strong);
  color: var(--sd-primary-contrast);
  text-decoration: none;
  transform: translateY(-1px);
}

.mi-btn .material-symbols-outlined {
  font-size: 20px;
  color: inherit;
}

.mi-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.mi-btn--whatsapp {
  background: #1fa855;
  color: #fff;
}

.mi-btn--whatsapp:hover:not(:disabled) {
  background: #128c4b;
  color: #fff;
}

.mi-ok {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: var(--sd-success);
}

.mi-ok .material-symbols-outlined {
  font-size: 20px;
  color: inherit;
}

.mi-link {
  padding: 0;
  border: 0;
  background: none;
  font-size: 0.9rem;
  color: var(--sd-muted);
  text-decoration: underline;
  cursor: pointer;
}

.mi-error {
  margin: 10px 0 0;
  font-size: 0.88rem;
  color: var(--sd-danger);
}

@media (max-width: 480px) {
  .mi-card {
    flex-direction: column;
    gap: 14px;
    padding: 20px;
  }
}
</style>
